import axios from 'axios';
import API from "../config/API";

// Beu agent — the salon/SE side assistant.
//
// House convention: the token rides the `accessToken` header, because
// authenticateBearer reads req.get('accessToken'). `Authorization` carries the
// gateway Basic pair on every request and is NOT the user's token — reading
// the wrong one is how the concierge treated logged-in clients as guests.
//
// Unlike the concierge there is no unauthenticated call here. Every endpoint
// on this side reads the salon's own money and clients.
function headers() {
    const accessToken = localStorage.getItem('accessToken');
    const h = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA=="
    };
    if (accessToken) h.accessToken = `${accessToken}`;
    return h;
}

// What this account may use. The thread renders NOTHING unless this says yes —
// the concierge's first test hit a greet-then-refuse bubble and was rightly
// roasted. A dead button is worse than no button.
function status() {
    return axios.get(API.AGENT_STATUS, { headers: headers() });
}

function chat(params) {
    return axios.post(API.AGENT_CHAT, params, { headers: headers() });
}

// A reply cut off because the page is going (a reload, or leaving the app)
// isn't a lost connection, and mustn't be reported as one
let leaving = false;
if (typeof window !== 'undefined') {
    const going = () => { leaving = true; };
    const back = (e) => { if (e && e.persisted) leaving = false; };
    window.addEventListener('pagehide', going);
    window.addEventListener('beforeunload', going);
    window.addEventListener('pageshow', back);
}

/**
 * Streamed turn. Calls onEvent for each server-sent event as it arrives.
 *
 * Uses fetch rather than axios because axios buffers the whole body — which
 * would turn streaming into one slow delivery, indistinguishable from not
 * streaming at all. Returns a function that aborts the request.
 */
function chatStream(params, onEvent) {
    const controller = new AbortController();
    let terminal = false;
    const emit = event => {
        if (controller.signal.aborted) return;
        if (event.type === 'done' || event.type === 'error') terminal = true;
        // The transport owns end: exactly once, including network failures.
        if (event.type !== 'end') onEvent(event);
    };
    const consume = frame => {
        const data = frame.split(/\r?\n/)
            .filter(line => line.startsWith('data:'))
            .map(line => line.slice(5).trimStart()).join('\n');
        if (!data) return;
        let event;
        try { event = JSON.parse(data); } catch (e) { return; }
        emit(event);
    };

    (async () => {
        let reader;
        try {
            const res = await fetch(API.AGENT_CHAT + '/stream', {
                method: 'POST', headers: headers(), body: JSON.stringify(params),
                signal: controller.signal
            });
            if (!res.ok || !res.body) {
                // A refusal with a reason (Bailey's free questions used up:
                // code 'bailey_quota') rides along so the thread can show a
                // card instead of a dead end.
                let body = null;
                try { body = await res.json(); } catch (e) { body = null; }
                const err = (body && body.error) || {};
                const data = (body && (err.data || body.data || body.responseData)) || null;
                emit({ type: 'error', message: err.responseMessage || (body && body.message) || 'Could not reach the assistant.', code: err.code || (data && data.code) || undefined, data });
                return;
            }
            reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            for (;;) {
                const { done, value } = await reader.read();
                buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
                const frames = buffer.split(/\r?\n\r?\n/);
                buffer = frames.pop() || '';
                frames.forEach(consume);
                if (done) {
                    if (buffer.trim()) consume(buffer);
                    break;
                }
            }
            if (!terminal && !leaving) emit({ type: 'error', message: 'The connection ended before Bailey finished. Please try again.' });
        } catch (err) {
            if (controller.signal.aborted || leaving) return;
            emit({ type: 'error', message: 'Lost connection to the assistant.' });
        } finally {
            if (reader) reader.releaseLock();
            if (!controller.signal.aborted) onEvent({ type: 'end' });
        }
    })();
    return () => controller.abort();
}

/**
 * The post studio's ask: { brief, reviews, history } -> { edit }.
 *
 * Stateless on the server: no tools, no thread in the Bailey tab. The studio
 * keeps its own short `history` per visit and sends it back each time. The
 * returned promise carries `cancel()` so a caller that gives up can stop it.
 */
function socialAsk(body) {
    const source = axios.CancelToken.source();
    const request = axios.post(API.AGENT_SOCIAL_ASK, body, { headers: headers(), cancelToken: source.token });
    request.cancel = () => source.cancel();
    return request;
}

// Newest first. `before` (ISO date) and `limit` page back through older
// threads; a server that ignores them returns the newest page again, which the
// list notices and treats as the end.
function sessions(params) {
    return axios.get(API.AGENT_SESSIONS, { headers: headers(), params: params || undefined });
}

// Ideas Bailey has for this salon, from its own data (see
// docs/BAILEY_TAB_BACKEND_HANDOVER.md). Not built on the server yet.
// Pin or unpin a conversation, so it stays at the top of the list
// (see docs/BAILEY_TAB_BACKEND_HANDOVER.md). Not built on the server yet.
function pinSession(id, pinned) {
    return axios.post(`${API.AGENT_SESSIONS}/${encodeURIComponent(id)}/pin`, { pinned: !!pinned }, { headers: headers() });
}

function ideas() {
    return axios.get(API.AGENT_IDEAS, { headers: headers() });
}

// The salon at a glance: the facts the home page's cards word themselves from.
function glance() {
    return axios.get(API.AGENT_GLANCE, { headers: headers() });
}

// Soft delete: hidden from this account's list, kept server-side for training.
// Bailey's Playbook: one pick and two more from this person's situation
// (null when it's off for them). docs/bailey-playbook.md on the server.
function playbook(params = {}) {
    return axios.get(API.AGENT_PLAYBOOK, { headers: headers(), params });
}

// What was shown, chosen (A, B or C) or put away, so it isn't nagged about
function playbookFeedback(body) {
    return axios.post(`${API.AGENT_PLAYBOOK}/feedback`, body, { headers: headers() });
}

// Ask the owner for something (a self-employed stylist: "introduce me")
function playbookRequest(body) {
    return axios.post(`${API.AGENT_PLAYBOOK}/request`, body, { headers: headers() });
}

function deleteSession(sessionId) {
    return axios.delete(`${API.AGENT_SESSIONS}/${encodeURIComponent(sessionId)}`, { headers: headers() });
}

// Undo a delete (the server allows it for a few minutes after).
function restoreSession(sessionId) {
    return axios.post(`${API.AGENT_SESSIONS}/${encodeURIComponent(sessionId)}/restore`, {}, { headers: headers() });
}

function transcript(sessionId) {
    return axios.get(`${API.AGENT_SESSIONS}/${encodeURIComponent(sessionId)}`, { headers: headers() });
}

function setEnabled(enabled) {
    return axios.post(API.AGENT_SETTINGS, { enabled }, { headers: headers() });
}

function setAppearance(appearance) {
    return axios.post(API.AGENT_APPEARANCE, appearance, { headers: headers() });
}

/**
 * Everything waiting on a person — approvals AND questions, one list.
 * Replaces pendingApprovals, which is kept only until nothing calls it.
 */
function asks() {
    return axios.get(API.AGENT_ASKS, { headers: headers() });
}

function answerAsk(id, value) {
    return axios.post(`${API.AGENT_ASKS}/${encodeURIComponent(id)}/answer`,
        { value }, { headers: headers() });
}

function dismissAsk(id, reason) {
    return axios.post(`${API.AGENT_ASKS}/${encodeURIComponent(id)}/dismiss`,
        { reason }, { headers: headers() });
}

function drafts() {
    return axios.get(API.AGENT_DRAFTS, { headers: headers() });
}

// One draft with its content, fetched only when someone opens it — the list
// stays light because email blocks are large.
function draft(kind, id) {
    return axios.get(
        `${API.AGENT_DRAFTS}/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`,
        { headers: headers() });
}

function goals() {
    return axios.get(API.AGENT_GOALS, { headers: headers() });
}

function goalThread(id) {
    return axios.get(`${API.AGENT_GOALS}/${encodeURIComponent(id)}/thread`, { headers: headers() });
}

function setGoalStatus(id, status) {
    return axios.post(`${API.AGENT_GOALS}/${encodeURIComponent(id)}/status`,
        { status }, { headers: headers() });
}

function pendingApprovals() {
    return axios.get(API.AGENT_APPROVALS, { headers: headers() });
}

// Approving runs the arguments the server PINNED when it proposed the action.
// Nothing about the action is re-sent from here, deliberately: the numbers on
// the card are a contract, not a suggestion.
function approve(id) {
    return axios.post(`${API.AGENT_APPROVALS}/${encodeURIComponent(id)}/approve`, {}, { headers: headers() });
}

function reject(id) {
    return axios.post(`${API.AGENT_APPROVALS}/${encodeURIComponent(id)}/reject`, {}, { headers: headers() });
}

export const agentService = {
    status,
    chat,
    chatStream,
    socialAsk,
    sessions,
    ideas,
    playbook,
    playbookFeedback,
    playbookRequest,
    glance,
    pinSession,
    deleteSession,
    restoreSession,
    transcript,
    setEnabled,
    setAppearance,
    asks,
    answerAsk,
    dismissAsk,
    drafts,
    draft,
    goals,
    goalThread,
    setGoalStatus,
    pendingApprovals,
    approve,
    reject
};

export default agentService;
